import React, { useCallback, useEffect } from 'react';
import { PlanInfo, VenuePlan } from '../venueplan';
import { TicketModel } from '../../../mvcapi/models/buyticket/submodels';
import tsRest from 'ts-rest-client';
import { selectPackageSeatFromTicketIds, selectSpecificTickets, unselectSpecificTickets } from '../../../mvcapi/buy-ticket-api';
import { some } from 'lodash';
import { useSeatsAvailability } from '../../../tsrestapi/hooks/use-seats-availability';
import { PlanSection } from 'mobile-venue-plan-js/lib/plan-section';
import TranslatorManager from 'ts-translate-js';
import { noop } from 'jquery';
import { useVenuePlanControl } from '../../../logic/hooks/use-venue-plan-control';
import { Vector2 } from 'peet-math';
import { useIFrameResize } from '../../../logic/hooks/utils';
import { useOneTimeLatch } from '../../../logic/hooks/use-one-time-latch';
import toastr from 'toastr';

const tr = TranslatorManager.boundTR;

interface TicketSelectionVenuePlanProps {
    eventDateId: number;
    selectedTickets: TicketModel[];

    // Package fields
    packageId?: number;
    packageSelectedEventDateIds?: number[];

    onPlanLoaded?: () => void;
    onSectionClick: (section: PlanSection) => void;
    onFingerSearch?: (eventDateId: number, pointOnPlan: Vector2, planEngine: any) => void;
    onSelectedTicketsAdded: (selectedTickets: TicketModel[]) => Promise<boolean>;
    onSelectedTicketsRemoved: (ticketIds: number[]) => void;
}

/*
{ TicketID: 123123, SeatID: 234234, Name: "Bibi", Position: { X: 100, Y: 100 }, Size: 15 }
*/
export function TicketSelectionVenuePlan({eventDateId, selectedTickets, packageId, packageSelectedEventDateIds, onPlanLoaded, onSectionClick, onFingerSearch, onSelectedTicketsAdded, onSelectedTicketsRemoved}: TicketSelectionVenuePlanProps) {
    useIFrameResize();
    const isPackageSale = !!packageId;
    
    let planInfo: PlanInfo = { sceneLocation: null };
    const [ availableSeats, gettingAvailableSeats, refreshSeatsAvailability ] = useSeatsAvailability({ 
        eventDateId: eventDateId, // This is the event date id or the main event date id of a package (For plan selection)

        packageId: packageId,
        packageSelectedDateIds: packageSelectedEventDateIds
        
    }); // If forceNoSeatMode, we don't want to go get the seats
    const [ isPlanLoaded ] = useOneTimeLatch(!gettingAvailableSeats); // We use that to make the difference between the first load and the further refresh
    const planControl = useVenuePlanControl();

    let processing = gettingAvailableSeats; // Add you stuff here

    // /legacy/ctl/ticket/searchticketatpinpoint

    // /legacy/ctl/chooseticketprice/pricelist

    // return tsapp.ctlPath + `/VenuePlan/LoadVenuePlanByVenueModelID?VenueModelID=` + venueModelId
    // New Path : 

    const venueImageURL = `${tsRest.getRESTBaseURL()}files/eventdates/${eventDateId}/venueplan`;

    const handleVenuePlanFingerSearch = (pointOnPlan: Vector2, planEngine) => {
        if(onFingerSearch)
            onFingerSearch(eventDateId, pointOnPlan, planEngine);
    };

    // Action menu for when available seats are not shown on plan
    let planContextMenu = {
        items: [
            {
                text: tr('venueplan.contextmenu.lookfortickethere'),
                action: handleVenuePlanFingerSearch
            },
            {
                text: tr('venueplan.contextmenu.cancel'),
                action: noop
            }
        ]
    };

    const handleSeatClick = async (ticketId: number) => {
        // Is it a ticket that we already have?
        const unselection = some(selectedTickets, ['TicketID', ticketId]);

        if(unselection) {
            //let doRemove = true;
            if(onSelectedTicketsRemoved) // If there<s a call back, wait to see if the user accept the unselection
                onSelectedTicketsRemoved([ticketId]);            
        } else {
            let tickets: TicketModel[], error: string;
            // TODO: Gerer les forfaits ici
            if(isPackageSale) {
                [tickets, error] = await selectPackageSeatFromTicketIds(packageId, eventDateId, [ticketId], packageSelectedEventDateIds);
            } else {
                [tickets, error] = await selectSpecificTickets(eventDateId, [ticketId]);
            }
            

            if(tickets.length > 0) {
                if(onSelectedTicketsAdded) {
                    const keepTicket = await onSelectedTicketsAdded(tickets);
    
                    if(keepTicket) {
                        planControl.moveToXYZoomAtLeast({ x: tickets[0].Position.X, y: tickets[0].Position.Y }, 1, 1000); // In case of a package, Ticket [0] should be in our base event.
                    }else {
                        await unselectSpecificTickets([ticketId]);
                    }
                }
            } else {
                // Force a plan refresh so the user has the latest seat status
                refreshSeatsAvailability();
                toastr.error(error);
            }
        }
    };

    // Remote control
    const handleRefreshSeatsOnPlan = useCallback(() => { refreshSeatsAvailability(); }, [refreshSeatsAvailability]);
    useEffect(() => {
        if(planControl) {
            planControl.onRefreshSeatsOnPlan(handleRefreshSeatsOnPlan);
        }
        return () => {
            if(planControl) {
                planControl.offRefreshSeatsOnPlan(handleRefreshSeatsOnPlan);
            }
        };
    }, [planControl, handleRefreshSeatsOnPlan]);

    const selTicketsGAExcluded = selectedTickets.filter(ticket => !ticket.IsGA);

    return <div>
        {isPlanLoaded && <VenuePlan
            eventDateId={eventDateId}
            venueImageURL={venueImageURL}
            tickets={selTicketsGAExcluded}
            planInfo={planInfo}
            autoFocusSeats={false}
            onPlanLoaded={onPlanLoaded}
            onSeatClick={handleSeatClick}
            onSectionClick={onSectionClick}
            contextMenu={planContextMenu}
            processing={processing}
            engineSettings={{ showDebugInfo: false }}
            availableSeats={availableSeats}
            remoteControl={planControl}
            showHowToUsePlanShown={false}
        />}
    </div>;
}
